"use client"

import { useState, useEffect } from "react"
import { Form, Button, Card, Alert, OverlayTrigger, Tooltip } from "react-bootstrap"
import Swal from "sweetalert2"
import DashNavbar from "./DashNavbar"
import Sidebar from "./Sidebar"
import "../css/EditDashboard.css"; // Ensure this path is correct

const API_URL = "https://raasbackend.vetrinahealthcare.com/company-info/"

const apiToFrontendData = (apiData) => ({
  whatIsVetrina: apiData.what_is_vetrina || "",
  whatIsVetzone: apiData.what_is_vetzone || "",
  aboutVetrina: apiData.about_vetrina || "",
  vision: apiData.vision || "",
  mission: Array.isArray(apiData.mission) ? apiData.mission : ["", ""],
  whyWorkWithVetrina: apiData.why_work_with_vetrina || "",
})

const frontendToApiData = (formData) => ({
  what_is_vetrina: formData.whatIsVetrina,
  what_is_vetzone: formData.whatIsVetzone,
  about_vetrina: formData.aboutVetrina,
  vision: formData.vision,
  mission: formData.mission.filter((item) => item && item.trim().length > 0),
  why_work_with_vetrina: formData.whyWorkWithVetrina,
})

const EditDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [activeSection, setActiveSection] = useState(0)
  const [formData, setFormData] = useState({
    whatIsVetrina: "", whatIsVetzone: "", aboutVetrina: "",
    vision: "", mission: ["", ""], whyWorkWithVetrina: "",
  })
  
  const calculateCompletion = () => {
    const fields = [
      formData.whatIsVetrina, formData.whatIsVetzone, formData.aboutVetrina,
      formData.vision, formData.mission[0], formData.mission[1], formData.whyWorkWithVetrina,
    ]
    const completed = fields.filter((field) => field && field.trim().length > 0).length
    return Math.round((completed / fields.length) * 100)
  }

  const getFieldStatus = (fieldValue) => (fieldValue && fieldValue.trim().length > 0 ? "completed" : "incomplete")

  const fetchDashboardContent = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(API_URL)
      if (response.ok) {
        const apiData = await response.json()
        const frontendData = apiToFrontendData(apiData)
        if (!Array.isArray(frontendData.mission)) frontendData.mission = ["", ""]
        while (frontendData.mission.length < 2) frontendData.mission.push("")
        frontendData.mission = frontendData.mission.slice(0, 2)
        setFormData(frontendData)
      } else if (response.status !== 404) {
        setError("Could not load existing content. You can create new content by saving.")
      }
    } catch (err) {
      setError("Failed to load dashboard content. Please check your network and try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardContent()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleMissionChange = (index, value) => {
    setFormData((prev) => {
      const newMission = [...prev.mission]
      newMission[index] = value
      return { ...prev, mission: newMission }
    })
  }

  const validateForm = () => {
    const requiredFields = [
      { field: formData.whatIsVetrina, name: "Why Vetrina" }, { field: formData.whatIsVetzone, name: "Why VetZone" },
      { field: formData.aboutVetrina, name: "About Vetrina" }, { field: formData.vision, name: "Vision" },
      { field: formData.mission[0], name: "Mission Point 1" }, { field: formData.mission[1], name: "Mission Point 2" },
      { field: formData.whyWorkWithVetrina, name: "Why Work With Vetrina" },
    ]
    const emptyFields = requiredFields.filter(({ field }) => !field || field.trim().length === 0)
    if (emptyFields.length > 0) {
      Swal.fire("Required Fields Missing", `Please fill in: ${emptyFields.map(({ name }) => name).join(", ")}`, "warning")
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setSaving(true)
    setError(null)
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(frontendToApiData(formData)),
      })
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || "Server error")
      Swal.fire("Success!", "Company information has been updated.", "success")
      fetchDashboardContent()
    } catch (err) {
      setError(`Failed to update: ${err.message}`)
      Swal.fire("Error!", `Failed to update: ${err.message}`, "error")
    } finally {
      setSaving(false)
    }
  }

  const sections = [
    { id: 0, title: "Company Overview", icon: "fas fa-building", color: "#6366f1" },
    { id: 1, title: "Vision & Mission", icon: "fas fa-bullseye", color: "#8b5cf6" },
    { id: 2, title: "Partnership", icon: "fas fa-handshake", color: "#06b6d4" },
  ]

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <div className="main">
          <div className="dashboard-container">
            <div className="dashboard-header">
              <div className="header-content">
                <div className="header-left">
                  <div className="header-icon-wrapper">
                    <div className="header-icon"><i className="fas fa-edit"></i></div>
                    <div className="header-pulse"></div>
                  </div>
                  <div>
                    <h1 className="header-title">Company Information Editor</h1>
                    <p className="header-subtitle">Manage your company's core information</p>
                  </div>
                </div>
                <div className="header-right">
                  <div className="completion-widget">
                    <div className="completion-circle">
                      <svg className="completion-svg" viewBox="0 0 36 36">
                        <path className="completion-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                        <path className="completion-progress" strokeDasharray={`${calculateCompletion()}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                      </svg>
                      <div className="completion-text">{calculateCompletion()}%</div>
                    </div>
                    <span className="completion-label">Complete</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="section-navigation">
              {sections.map((section) => (
                <button key={section.id} className={`nav-tab ${activeSection === section.id ? "active" : ""}`} onClick={() => setActiveSection(section.id)} style={{ "--tab-color": section.color }}>
                  <i className={section.icon}></i>
                  <span>{section.title}</span>
                </button>
              ))}
            </div>

            {error && <Alert variant="danger" className="error-alert"><i className="fas fa-exclamation-triangle me-2"></i>{error}</Alert>}

            <Form onSubmit={handleSubmit} className="dashboard-form">
              {activeSection === 0 && (
                <div className="section-content">
                  <div className="form-grid">
                    <div>
                      <Card className={`form-card ${getFieldStatus(formData.whatIsVetrina)}`}>
                        <Card.Body className="card-body-custom">
                          <Form.Group>
                            <Form.Label className="form-label-custom">Why Vetrina <span className="required-indicator">*</span></Form.Label>
                            {/* MODIFIED: Increased rows for more height */}
                            <Form.Control as="textarea" rows={16} name="whatIsVetrina" value={formData.whatIsVetrina} onChange={handleInputChange} placeholder="Describe what makes Vetrina unique..." className="form-textarea-custom" required/>
                            <div className="char-counter">{formData.whatIsVetrina.length} characters</div>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </div>
                    <div>
                      <Card className={`form-card ${getFieldStatus(formData.whatIsVetzone)}`}>
                        <Card.Body className="card-body-custom">
                          <Form.Group>
                            <Form.Label className="form-label-custom">Why VetZone <span className="required-indicator">*</span></Form.Label>
                            {/* MODIFIED: Increased rows for more height */}
                            <Form.Control as="textarea" rows={16} name="whatIsVetzone" value={formData.whatIsVetzone} onChange={handleInputChange} placeholder="Explain the VetZone platform..." className="form-textarea-custom" required/>
                            <div className="char-counter">{formData.whatIsVetzone.length} characters</div>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </div>
                    <div className="grid-col-span-2">
                      <Card className={`form-card ${getFieldStatus(formData.aboutVetrina)}`}>
                        <Card.Body className="card-body-custom">
                          <Form.Group>
                            <Form.Label className="form-label-custom">About Vetrina <span className="required-indicator">*</span></Form.Label>
                            <Form.Control as="textarea" rows={12} name="aboutVetrina" value={formData.aboutVetrina} onChange={handleInputChange} placeholder="Provide a comprehensive company description..." className="form-textarea-custom" required/>
                            <div className="char-counter">{formData.aboutVetrina.length} characters</div>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
              {activeSection === 1 && (
                <div className="section-content">
                  <div className="form-grid">
                    <div className="grid-col-span-2">
                      <Card className={`form-card ${getFieldStatus(formData.vision)}`}>
                        <Card.Body className="card-body-custom">
                          <Form.Group>
                            <Form.Label className="form-label-custom">Vision Statement <span className="required-indicator">*</span></Form.Label>
                            <Form.Control as="textarea" rows={10} name="vision" value={formData.vision} onChange={handleInputChange} placeholder="Describe your company's long-term vision..." className="form-textarea-custom" required/>
                            <div className="char-counter">{formData.vision.length} characters</div>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </div>
                    <div className="grid-col-span-2">
                      <Card className="form-card">
                        <Card.Body className="card-body-custom">
                            <Form.Label className="form-label-custom">Mission Points</Form.Label>
                            {[0, 1].map((index) => (
                                <Form.Group key={index} className="mb-3">
                                <Form.Control type="text" value={formData.mission[index] || ""} onChange={(e) => handleMissionChange(index, e.target.value)} placeholder={`Enter mission point ${index + 1}...`} className={`form-input-custom ${getFieldStatus(formData.mission[index])}`} required/>
                                </Form.Group>
                            ))}
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
              {activeSection === 2 && (
                <div className="section-content">
                  <div className="form-grid">
                    <div className="grid-col-span-2">
                      <Card className={`form-card ${getFieldStatus(formData.whyWorkWithVetrina)}`}>
                        <Card.Body className="card-body-custom">
                          <Form.Group>
                            <Form.Label className="form-label-custom">Why Work With Vetrina <span className="required-indicator">*</span></Form.Label>
                            <Form.Control as="textarea" rows={12} name="whyWorkWithVetrina" value={formData.whyWorkWithVetrina} onChange={handleInputChange} placeholder="Explain the benefits of partnering with Vetrina..." className="form-textarea-custom" required/>
                            <div className="char-counter">{formData.whyWorkWithVetrina.length} characters</div>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
              <div className="action-buttons">
                <Button variant="outline-secondary" className="btn-reset" onClick={fetchDashboardContent} disabled={saving}>
                  <i className="fas fa-undo me-2"></i>Reset Changes
                </Button>
                <Button type="submit" disabled={saving} className="btn-save">
                  {saving ? (<><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>) : (<><i className="fas fa-save me-2"></i>Save Changes</>)}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  )
}

export default EditDashboard
// "use client"

// import { useState, useEffect } from "react"
// import { Form, Button, Card, Row, Col, Alert, OverlayTrigger, Tooltip } from "react-bootstrap"
// import Swal from "sweetalert2"
// import DashNavbar from "./DashNavbar"
// import Sidebar from "./Sidebar"

// // Define the API endpoint
// const API_URL = "https://raasbackend.vetrinahealthcare.com/company-info/"

// // Helper function to convert API snake_case to frontend camelCase
// const apiToFrontendData = (apiData) => {
//   return {
//     whatIsVetrina: apiData.what_is_vetrina || "",
//     whatIsVetzone: apiData.what_is_vetzone || "",
//     aboutVetrina: apiData.about_vetrina || "",
//     vision: apiData.vision || "",
//     mission: Array.isArray(apiData.mission) ? apiData.mission : ["", ""],
//     whyWorkWithVetrina: apiData.why_work_with_vetrina || "",
//   }
// }

// // Helper function to convert frontend camelCase to API snake_case
// const frontendToApiData = (formData) => {
//   // Filter out empty mission points
//   const missionFiltered = formData.mission.filter((item) => item && item.trim().length > 0)

//   return {
//     what_is_vetrina: formData.whatIsVetrina,
//     what_is_vetzone: formData.whatIsVetzone,
//     about_vetrina: formData.aboutVetrina,
//     vision: formData.vision,
//     mission: missionFiltered,
//     why_work_with_vetrina: formData.whyWorkWithVetrina,
//   }
// }

// const EditDashboard = () => {
//   const [loading, setLoading] = useState(true)
//   const [saving, setSaving] = useState(false)
//   const [error, setError] = useState(null)
//   const [activeSection, setActiveSection] = useState(0)
//   const [formData, setFormData] = useState({
//     whatIsVetrina: "",
//     whatIsVetzone: "",
//     aboutVetrina: "",
//     vision: "",
//     mission: ["", ""],
//     whyWorkWithVetrina: "",
//   })

//   // Calculate completion percentage
//   const calculateCompletion = () => {
//     const requiredFields = [
//       formData.whatIsVetrina,
//       formData.whatIsVetzone,
//       formData.aboutVetrina,
//       formData.vision,
//       formData.mission[0], // Mission Point 1 - Required
//       formData.mission[1], // Mission Point 2 - Required
//       formData.whyWorkWithVetrina,
//     ]
//     const completed = requiredFields.filter((field) => field && field.trim().length > 0).length
//     return Math.round((completed / requiredFields.length) * 100)
//   }

//   // Get field completion status for visual indicators
//   const getFieldStatus = (fieldValue) => {
//     return fieldValue && fieldValue.trim().length > 0 ? "completed" : "incomplete"
//   }

//   const defaultFrontendContent = {
//     whatIsVetrina: "Vetrina Healthcare is a leading animal nutrition and healthcare company...",
//     whatIsVetzone: "Vetzone is our specialized platform that connects veterinary professionals...",
//     aboutVetrina: "Vetrina Healthcare is the innovation-driven animal nutrition and health care company in India...",
//     vision: "To be a leading solution provider company...",
//     mission: [
//       "To make livestock farming simple and profitable",
//       "To enhance the health of Pet and happiness of Pet-parents",
//     ],
//     whyWorkWithVetrina: "Veterinary practices and animal health are interesting fields...",
//   }

//   useEffect(() => {
//     fetchDashboardContent()
//   }, [])

//   const fetchDashboardContent = async () => {
//     setLoading(true)
//     setError(null)
//     try {
//       const response = await fetch(API_URL)
//       if (response.ok) {
//         const apiData = await response.json()
//         const frontendData = apiToFrontendData(apiData)

//         // Ensure mission array has exactly 2 elements for the form
//         if (!Array.isArray(frontendData.mission)) {
//           frontendData.mission = ["", ""]
//         } else {
//           // Pad with empty strings to ensure we have 2 elements
//           while (frontendData.mission.length < 2) {
//             frontendData.mission.push("")
//           }
//           // Limit to 2 elements for the form
//           frontendData.mission = frontendData.mission.slice(0, 2)
//         }

//         setFormData(frontendData)
//       } else {
//         console.warn(`Failed to fetch content (Status: ${response.status}). Using default form structure.`)
//         setFormData(defaultFrontendContent)
//         if (response.status !== 404) {
//           setError("Could not load existing content. You can create new content by saving.")
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching dashboard content:", error)
//       setError("Failed to load dashboard content. Displaying default structure. Please try saving.")
//       setFormData(defaultFrontendContent)
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleInputChange = (e) => {
//     const { name, value } = e.target
//     setFormData((prevData) => ({
//       ...prevData,
//       [name]: value,
//     }))
//   }

//   const handleMissionChange = (index, value) => {
//     setFormData((prevData) => {
//       const updatedMission = [...prevData.mission]
//       updatedMission[index] = value
//       return {
//         ...prevData,
//         mission: updatedMission,
//       }
//     })
//   }

//   // Validate required fields before submission
//   const validateForm = () => {
//     const requiredFields = [
//       { field: formData.whatIsVetrina, name: "Why Vetrina" },
//       { field: formData.whatIsVetzone, name: "Why VetZone" },
//       { field: formData.aboutVetrina, name: "About Vetrina" },
//       { field: formData.vision, name: "Vision" },
//       { field: formData.mission[0], name: "Mission Point 1" },
//       { field: formData.mission[1], name: "Mission Point 2" },
//       { field: formData.whyWorkWithVetrina, name: "Why Work With Vetrina" },
//     ]

//     const emptyFields = requiredFields.filter(({ field }) => !field || field.trim().length === 0)

//     if (emptyFields.length > 0) {
//       const fieldNames = emptyFields.map(({ name }) => name).join(", ")
//       Swal.fire({
//         title: "Required Fields Missing",
//         text: `Please fill in the following required fields: ${fieldNames}`,
//         icon: "warning",
//         confirmButtonColor: "#f59e0b",
//         background: "#ffffff",
//         customClass: {
//           popup: "swal-custom-popup",
//           title: "swal-custom-title",
//           content: "swal-custom-content",
//         },
//       })
//       return false
//     }
//     return true
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()

//     // Validate required fields
//     if (!validateForm()) {
//       return
//     }

//     setSaving(true)
//     setError(null)

//     // Prepare mission payload - only include non-empty mission points
//     const missionPayload = formData.mission.map((mission) => mission.trim()).filter((mission) => mission.length > 0)

//     const apiPayload = frontendToApiData({
//       ...formData,
//       mission: missionPayload,
//     })

//     try {
//       const response = await fetch(API_URL, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(apiPayload),
//       })

//       if (response.ok) {
//         Swal.fire({
//           title: "Success!",
//           text: "Company information has been updated successfully.",
//           icon: "success",
//           confirmButtonColor: "#6366f1",
//           background: "#ffffff",
//           customClass: {
//             popup: "swal-custom-popup",
//             title: "swal-custom-title",
//             content: "swal-custom-content",
//           },
//         })
//         fetchDashboardContent()
//       } else {
//         const errorData = await response.json().catch(() => ({ detail: "Unknown server error or non-JSON response" }))
//         let errorMessage = errorData.detail || Object.values(errorData).flat().join(" ")
//         if (!errorMessage) {
//           errorMessage = `Failed to update (Status: ${response.status})`
//         }
//         throw new Error(errorMessage)
//       }
//     } catch (error) {
//       console.error("Error updating company information:", error)
//       const message = error.message || "An unexpected error occurred."
//       setError(`Failed to update company information: ${message}`)
//       Swal.fire({
//         title: "Error!",
//         text: `Failed to update company information: ${message}`,
//         icon: "error",
//         confirmButtonColor: "#ef4444",
//       })
//     } finally {
//       setSaving(false)
//     }
//   }

//   const sections = [
//     { id: 0, title: "Company Overview", icon: "fas fa-building", color: "#6366f1" },
//     { id: 1, title: "Vision & Mission", icon: "fas fa-bullseye", color: "#8b5cf6" },
//     { id: 2, title: "Partnership", icon: "fas fa-handshake", color: "#06b6d4" },
//   ]

//   if (loading) {
//     return (
//       <>
//         <DashNavbar />
//         <div className="wrapper">
//           <Sidebar />
//           <div className="main">
//             <div className="loading-overlay">
//               <div className="loading-container">
//                 <div className="loading-spinner">
//                   <div className="spinner-ring"></div>
//                   <div className="spinner-ring"></div>
//                   <div className="spinner-ring"></div>
//                 </div>
//                 <h3 className="loading-title">Preparing Your Dashboard</h3>
//                 <p className="loading-subtitle">Setting up the content editor interface...</p>
//                 <div className="loading-progress">
//                   <div className="loading-progress-bar"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </>
//     )
//   }

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           <div className="dashboard-container">
//             {/* Enhanced Header */}
//             <div className="dashboard-header">
//               <div className="header-content">
//                 <div className="header-left">
//                   <div className="header-icon-wrapper">
//                     <div className="header-icon">
//                       <i className="fas fa-edit"></i>
//                     </div>
//                     <div className="header-pulse"></div>
//                   </div>
//                   <div className="header-text">
//                     <h1 className="header-title">Company Information Editor</h1>
//                     <p className="header-subtitle">Manage and update your company's core information</p>
//                   </div>
//                 </div>
//                 <div className="header-right">
//                   <div className="completion-widget">
//                     <div className="completion-circle">
//                       <svg className="completion-svg" viewBox="0 0 36 36">
//                         <path
//                           className="completion-bg"
//                           d="M18 2.0845
//                             a 15.9155 15.9155 0 0 1 0 31.831
//                             a 15.9155 15.9155 0 0 1 0 -31.831"
//                         />
//                         <path
//                           className="completion-progress"
//                           strokeDasharray={`${calculateCompletion()}, 100`}
//                           d="M18 2.0845
//                             a 15.9155 15.9155 0 0 1 0 31.831
//                             a 15.9155 15.9155 0 0 1 0 -31.831"
//                         />
//                       </svg>
//                       <div className="completion-text">{calculateCompletion()}%</div>
//                     </div>
//                     <span className="completion-label">Complete</span>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Navigation Tabs */}
//             <div className="section-navigation">
//               {sections.map((section) => (
//                 <button
//                   key={section.id}
//                   className={`nav-tab ${activeSection === section.id ? "active" : ""}`}
//                   onClick={() => setActiveSection(section.id)}
//                   style={{ "--tab-color": section.color }}
//                 >
//                   <i className={section.icon}></i>
//                   <span>{section.title}</span>
//                 </button>
//               ))}
//             </div>

//             {error && (
//               <Alert variant="danger" className="error-alert">
//                 <div className="error-content">
//                   <i className="fas fa-exclamation-triangle error-icon"></i>
//                   <div className="error-text">
//                     <strong>Attention Required</strong>
//                     <p>{error}</p>
//                   </div>
//                 </div>
//               </Alert>
//             )}

//             <Form onSubmit={handleSubmit} className="dashboard-form">
//               {/* Company Overview Section */}
//               {activeSection === 0 && (
//                 <div className="section-content animate-in">
//                   <div className="section-header">
//                     <h2 className="section-title">
//                       <i className="fas fa-building section-icon"></i>
//                       Company Overview
//                     </h2>
//                     <p className="section-description">Define your company's core identity and value propositions</p>
//                   </div>

//                   <Row className="g-4">
//                     <Col lg={6}>
//                       <Card className={`form-card ${getFieldStatus(formData.whatIsVetrina)}`}>
//                         <div className="card-header-custom">
//                           <div className="card-indicator purple"></div>
//                           <h5 className="card-title">
//                             Why Vetrina
//                             <span className="required-indicator">*</span>
//                           </h5>
//                           <OverlayTrigger
//                             placement="top"
//                             overlay={<Tooltip>Explain what makes Vetrina unique (Required)</Tooltip>}
//                           >
//                             <i className="fas fa-info-circle card-info"></i>
//                           </OverlayTrigger>
//                         </div>
//                         <Card.Body className="card-body-custom">
//                           <Form.Group>
//                             <Form.Label className="form-label-custom">Content</Form.Label>
//                             <Form.Control
//                               as="textarea"
//                               rows={6}
//                               name="whatIsVetrina"
//                               value={formData.whatIsVetrina}
//                               onChange={handleInputChange}
//                               placeholder="Describe what makes Vetrina special and unique in the market..."
//                               className="form-textarea-custom"
//                               required
//                             />
//                             <div className="char-counter">{formData.whatIsVetrina.length} characters</div>
//                           </Form.Group>
//                         </Card.Body>
//                       </Card>
//                     </Col>

//                     <Col lg={6}>
//                       <Card className={`form-card ${getFieldStatus(formData.whatIsVetzone)}`}>
//                         <div className="card-header-custom">
//                           <div className="card-indicator green"></div>
//                           <h5 className="card-title">
//                             Why VetZone
//                             <span className="required-indicator">*</span>
//                           </h5>
//                           <OverlayTrigger
//                             placement="top"
//                             overlay={<Tooltip>Describe VetZone platform benefits (Required)</Tooltip>}
//                           >
//                             <i className="fas fa-info-circle card-info"></i>
//                           </OverlayTrigger>
//                         </div>
//                         <Card.Body className="card-body-custom">
//                           <Form.Group>
//                             <Form.Label className="form-label-custom">Content</Form.Label>
//                             <Form.Control
//                               as="textarea"
//                               rows={6}
//                               name="whatIsVetzone"
//                               value={formData.whatIsVetzone}
//                               onChange={handleInputChange}
//                               placeholder="Explain the benefits and features of the VetZone platform..."
//                               className="form-textarea-custom"
//                               required
//                             />
//                             <div className="char-counter">{formData.whatIsVetzone.length} characters</div>
//                           </Form.Group>
//                         </Card.Body>
//                       </Card>
//                     </Col>

//                     <Col xs={12}>
//                       <Card className={`form-card ${getFieldStatus(formData.aboutVetrina)}`}>
//                         <div className="card-header-custom">
//                           <div className="card-indicator blue"></div>
//                           <h5 className="card-title">
//                             About Vetrina
//                             <span className="required-indicator">*</span>
//                           </h5>
//                           <OverlayTrigger
//                             placement="top"
//                             overlay={<Tooltip>Comprehensive company description (Required)</Tooltip>}
//                           >
//                             <i className="fas fa-info-circle card-info"></i>
//                           </OverlayTrigger>
//                         </div>
//                         <Card.Body className="card-body-custom">
//                           <Form.Group>
//                             <Form.Label className="form-label-custom">Company Description</Form.Label>
//                             <Form.Control
//                               as="textarea"
//                               rows={8}
//                               name="aboutVetrina"
//                               value={formData.aboutVetrina}
//                               onChange={handleInputChange}
//                               placeholder="Provide a comprehensive description of Vetrina Healthcare, its history, achievements, and market position..."
//                               className="form-textarea-custom"
//                               required
//                             />
//                             <div className="char-counter">{formData.aboutVetrina.length} characters</div>
//                           </Form.Group>
//                         </Card.Body>
//                       </Card>
//                     </Col>
//                   </Row>
//                 </div>
//               )}

//               {/* Vision & Mission Section */}
//               {activeSection === 1 && (
//                 <div className="section-content animate-in">
//                   <div className="section-header">
//                     <h2 className="section-title">
//                       <i className="fas fa-bullseye section-icon"></i>
//                       Vision & Mission
//                     </h2>
//                     <p className="section-description">Define your company's future aspirations and core objectives</p>
//                   </div>

//                   <Row className="g-4">
//                     <Col lg={6}>
//                       <Card className={`form-card ${getFieldStatus(formData.vision)}`}>
//                         <div className="card-header-custom">
//                           <div className="card-indicator purple"></div>
//                           <h5 className="card-title">
//                             <i className="fas fa-eye me-2"></i>
//                             Vision Statement
//                             <span className="required-indicator">*</span>
//                           </h5>
//                         </div>
//                         <Card.Body className="card-body-custom">
//                           <Form.Group>
//                             <Form.Label className="form-label-custom">Company Vision</Form.Label>
//                             <Form.Control
//                               as="textarea"
//                               rows={6}
//                               name="vision"
//                               value={formData.vision}
//                               onChange={handleInputChange}
//                               placeholder="Describe your company's long-term vision and aspirations..."
//                               className="form-textarea-custom"
//                               required
//                             />
//                             <div className="char-counter">{formData.vision.length} characters</div>
//                           </Form.Group>
//                         </Card.Body>
//                       </Card>
//                     </Col>

//                     <Col lg={6}>
//                       <Card className="form-card">
//                         <div className="card-header-custom">
//                           <div className="card-indicator orange"></div>
//                           <h5 className="card-title">
//                             <i className="fas fa-bullseye me-2"></i>
//                             Mission Points
//                           </h5>
//                         </div>
//                         <Card.Body className="card-body-custom">
//                           {[0, 1].map((index) => (
//                             <Form.Group key={index} className={index === 0 ? "mb-3" : "mb-0"}>
//                               <Form.Label className="form-label-custom">
//                                 Mission Point {index + 1}
//                                 <span className="required-indicator">*</span>
//                               </Form.Label>
//                               <Form.Control
//                                 type="text"
//                                 value={formData.mission[index] || ""}
//                                 onChange={(e) => handleMissionChange(index, e.target.value)}
//                                 placeholder={`Enter mission point ${index + 1}...`}
//                                 className={`form-input-custom ${getFieldStatus(formData.mission[index])}`}
//                                 required
//                               />
//                             </Form.Group>
//                           ))}
//                         </Card.Body>
//                       </Card>
//                     </Col>
//                   </Row>
//                 </div>
//               )}

//               {/* Partnership Section */}
//               {activeSection === 2 && (
//                 <div className="section-content animate-in">
//                   <div className="section-header">
//                     <h2 className="section-title">
//                       <i className="fas fa-handshake section-icon"></i>
//                       Partnership
//                     </h2>
//                     <p className="section-description">Highlight the benefits of partnering with your company</p>
//                   </div>

//                   <Card className={`form-card ${getFieldStatus(formData.whyWorkWithVetrina)}`}>
//                     <div className="card-header-custom">
//                       <div className="card-indicator cyan"></div>
//                       <h5 className="card-title">
//                         Why Work With Vetrina
//                         <span className="required-indicator">*</span>
//                       </h5>
//                       <OverlayTrigger
//                         placement="top"
//                         overlay={<Tooltip>Explain partnership benefits and advantages (Required)</Tooltip>}
//                       >
//                         <i className="fas fa-info-circle card-info"></i>
//                       </OverlayTrigger>
//                     </div>
//                     <Card.Body className="card-body-custom">
//                       <Form.Group>
//                         <Form.Label className="form-label-custom">Partnership Benefits</Form.Label>
//                         <Form.Control
//                           as="textarea"
//                           rows={8}
//                           name="whyWorkWithVetrina"
//                           value={formData.whyWorkWithVetrina}
//                           onChange={handleInputChange}
//                           placeholder="Explain the compelling reasons why businesses should choose to partner with Vetrina..."
//                           className="form-textarea-custom"
//                           required
//                         />
//                         <div className="char-counter">{formData.whyWorkWithVetrina.length} characters</div>
//                       </Form.Group>
//                     </Card.Body>
//                   </Card>
//                 </div>
//               )}

//               {/* Action Buttons */}
//               <div className="action-buttons">
//                 <Button
//                   variant="outline-secondary"
//                   className="btn-reset"
//                   onClick={() => fetchDashboardContent()}
//                   disabled={loading || saving}
//                 >
//                   <i className="fas fa-undo me-2"></i>
//                   Reset Form
//                 </Button>
//                 <Button type="submit" disabled={saving || loading} className="btn-save">
//                   {saving ? (
//                     <>
//                       <div className="btn-spinner"></div>
//                       Saving Changes...
//                     </>
//                   ) : (
//                     <>
//                       <i className="fas fa-save me-2"></i>
//                       Save Changes
//                     </>
//                   )}
//                 </Button>
//               </div>
//             </Form>
//           </div>
//         </div>
//       </div>

//       <style jsx>{`
//         /* Main Layout */
//         .main {
//           background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//           min-height: 100vh;
//           position: relative;
//         }

//         .main::before {
//           content: '';
//           position: absolute;
//           top: 0;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           background: 
//             radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
//             radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.15) 0%, transparent 50%),
//             radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.1) 0%, transparent 50%);
//         }

//         .dashboard-container {
//           position: relative;
//           z-index: 1;
//           padding: 2rem;
//           max-width: 1400px;
//           margin: 0 auto;
//         }

//         /* Enhanced Header */
//         .dashboard-header {
//           background: rgba(255, 255, 255, 0.95);
//           backdrop-filter: blur(20px);
//           border-radius: 24px;
//           padding: 2rem;
//           margin-bottom: 2rem;
//           box-shadow: 
//             0 20px 25px -5px rgba(0, 0, 0, 0.1),
//             0 10px 10px -5px rgba(0, 0, 0, 0.04);
//           border: 1px solid rgba(255, 255, 255, 0.2);
//         }

//         .header-content {
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//         }

//         .header-left {
//           display: flex;
//           align-items: center;
//         }

//         .header-icon-wrapper {
//           position: relative;
//           margin-right: 1.5rem;
//         }

//         .header-icon {
//           width: 64px;
//           height: 64px;
//           background: linear-gradient(135deg, #6366f1, #8b5cf6);
//           border-radius: 20px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
//           position: relative;
//           z-index: 2;
//         }

//         .header-icon i {
//           font-size: 28px;
//           color: white;
//         }

//         .header-pulse {
//           position: absolute;
//           top: 0;
//           left: 0;
//           width: 64px;
//           height: 64px;
//           border-radius: 20px;
//           background: linear-gradient(135deg, #6366f1, #8b5cf6);
//           opacity: 0.4;
//           animation: pulse 2s infinite;
//         }

//         @keyframes pulse {
//           0% { transform: scale(1); opacity: 0.4; }
//           50% { transform: scale(1.1); opacity: 0.2; }
//           100% { transform: scale(1); opacity: 0.4; }
//         }

//         .header-title {
//           font-size: 2.5rem;
//           font-weight: 800;
//           background: linear-gradient(135deg, #1f2937, #4f46e5);
//           -webkit-background-clip: text;
//           -webkit-text-fill-color: transparent;
//           margin: 0;
//           line-height: 1.2;
//         }

//         .header-subtitle {
//           color: #6b7280;
//           font-size: 1.1rem;
//           margin: 0.5rem 0 0 0;
//           font-weight: 500;
//         }

//         /* Completion Widget */
//         .completion-widget {
//           display: flex;
//           flex-direction: column;
//           align-items: center;
//         }

//         .completion-circle {
//           position: relative;
//           width: 80px;
//           height: 80px;
//         }

//         .completion-svg {
//           width: 100%;
//           height: 100%;
//           transform: rotate(-90deg);
//         }

//         .completion-bg {
//           fill: none;
//           stroke: #e5e7eb;
//           stroke-width: 2;
//         }

//         .completion-progress {
//           fill: none;
//           stroke: #6366f1;
//           stroke-width: 2;
//           stroke-linecap: round;
//           transition: stroke-dasharray 0.5s ease;
//         }

//         .completion-text {
//           position: absolute;
//           top: 50%;
//           left: 50%;
//           transform: translate(-50%, -50%);
//           font-size: 1.2rem;
//           font-weight: 700;
//           color: #1f2937;
//         }

//         .completion-label {
//           margin-top: 0.5rem;
//           font-size: 0.9rem;
//           color: #6b7280;
//           font-weight: 600;
//         }

//         /* Section Navigation */
//         .section-navigation {
//           display: flex;
//           background: rgba(255, 255, 255, 0.9);
//           backdrop-filter: blur(20px);
//           border-radius: 16px;
//           padding: 0.5rem;
//           margin-bottom: 2rem;
//           box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
//           border: 1px solid rgba(255, 255, 255, 0.2);
//         }

//         .nav-tab {
//           flex: 1;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           padding: 1rem 1.5rem;
//           border: none;
//           background: transparent;
//           border-radius: 12px;
//           font-weight: 600;
//           color: #6b7280;
//           transition: all 0.3s ease;
//           position: relative;
//           overflow: hidden;
//         }

//         .nav-tab i {
//           margin-right: 0.5rem;
//           font-size: 1.1rem;
//         }

//         .nav-tab.active {
//           background: var(--tab-color);
//           color: white;
//           box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
//           transform: translateY(-2px);
//         }

//         .nav-tab:not(.active):hover {
//           background: rgba(0, 0, 0, 0.05);
//           transform: translateY(-1px);
//         }

//         /* Error Alert */
//         .error-alert {
//           background: rgba(254, 242, 242, 0.95);
//           backdrop-filter: blur(20px);
//           border: 1px solid #fecaca;
//           border-radius: 16px;
//           margin-bottom: 2rem;
//           padding: 1.5rem;
//         }

//         .error-content {
//           display: flex;
//           align-items: flex-start;
//         }

//         .error-icon {
//           color: #ef4444;
//           font-size: 1.5rem;
//           margin-right: 1rem;
//           margin-top: 0.25rem;
//         }

//         .error-text strong {
//           color: #dc2626;
//           display: block;
//           margin-bottom: 0.5rem;
//         }

//         .error-text p {
//           color: #7f1d1d;
//           margin: 0;
//         }

//         /* Section Content */
//         .section-content {
//           animation: slideIn 0.5s ease-out;
//         }

//         @keyframes slideIn {
//           from {
//             opacity: 0;
//             transform: translateY(20px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }

//         .section-header {
//           text-align: center;
//           margin-bottom: 3rem;
//         }

//         .section-title {
//           font-size: 2.2rem;
//           font-weight: 700;
//           color: white;
//           margin-bottom: 1rem;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//         }

//         .section-icon {
//           margin-right: 1rem;
//           padding: 0.5rem;
//           background: rgba(255, 255, 255, 0.2);
//           border-radius: 12px;
//           font-size: 1.5rem;
//         }

//         .section-description {
//           color: rgba(255, 255, 255, 0.8);
//           font-size: 1.1rem;
//           max-width: 600px;
//           margin: 0 auto;
//         }

//         /* Form Cards */
//         .form-card {
//           background: rgba(255, 255, 255, 0.95);
//           backdrop-filter: blur(20px);
//           border: 1px solid rgba(255, 255, 255, 0.2);
//           border-radius: 20px;
//           box-shadow: 
//             0 20px 25px -5px rgba(0, 0, 0, 0.1),
//             0 10px 10px -5px rgba(0, 0, 0, 0.04);
//           transition: all 0.3s ease;
//           overflow: hidden;
//         }

//         .form-card:hover {
//           transform: translateY(-5px);
//           box-shadow: 
//             0 25px 50px -12px rgba(0, 0, 0, 0.15),
//             0 20px 25px -5px rgba(0, 0, 0, 0.1);
//         }

//         .form-card.completed {
//           border-left: 4px solid #10b981;
//         }

//         .form-card.incomplete {
//           border-left: 4px solid #f59e0b;
//         }

//         .card-header-custom {
//           background: linear-gradient(135deg, #f8fafc, #f1f5f9);
//           border-bottom: 1px solid rgba(0, 0, 0, 0.05);
//           padding: 1.5rem;
//           display: flex;
//           align-items: center;
//           justify-content: space-between;
//         }

//         .card-indicator {
//           width: 4px;
//           height: 24px;
//           border-radius: 2px;
//           margin-right: 1rem;
//         }

//         .card-indicator.purple { background: linear-gradient(135deg, #8b5cf6, #6366f1); }
//         .card-indicator.green { background: linear-gradient(135deg, #10b981, #059669); }
//         .card-indicator.blue { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
//         .card-indicator.orange { background: linear-gradient(135deg, #f59e0b, #d97706); }
//         .card-indicator.cyan { background: linear-gradient(135deg, #06b6d4, #0891b2); }

//         .card-title {
//           font-size: 1.3rem;
//           font-weight: 700;
//           color: #1f2937;
//           margin: 0;
//           display: flex;
//           align-items: center;
//         }

//         .required-indicator {
//           color: #ef4444;
//           margin-left: 0.25rem;
//           font-size: 1.1rem;
//         }

//         .card-info {
//           color: #6b7280;
//           cursor: help;
//           transition: color 0.2s ease;
//         }

//         .card-info:hover {
//           color: #4f46e5;
//         }

//         .card-body-custom {
//           padding: 2rem;
//         }

//         /* Form Elements */
//         .form-label-custom {
//           font-weight: 600;
//           color: #374151;
//           margin-bottom: 0.75rem;
//           font-size: 0.95rem;
//           display: block;
//         }

//         .form-textarea-custom,
//         .form-input-custom {
//           border: 2px solid #e5e7eb;
//           border-radius: 12px;
//           padding: 1rem;
//           font-size: 1rem;
//           line-height: 1.6;
//           transition: all 0.3s ease;
//           background: rgba(255, 255, 255, 0.8);
//           resize: none;
//         }

//         .form-textarea-custom:focus,
//         .form-input-custom:focus {
//           border-color: #6366f1;
//           box-shadow: 
//             0 0 0 3px rgba(99, 102, 241, 0.1),
//             0 4px 6px -1px rgba(0, 0, 0, 0.1);
//           background: white;
//           outline: none;
//         }

//         .form-input-custom.completed {
//           border-color: #10b981;
//         }

//         .form-input-custom.incomplete {
//           border-color: #f59e0b;
//         }

//         .char-counter {
//           text-align: right;
//           font-size: 0.85rem;
//           color: #9ca3af;
//           margin-top: 0.5rem;
//         }

//         /* Action Buttons */
//         .action-buttons {
//           display: flex;
//           justify-content: center;
//           gap: 1rem;
//           margin-top: 3rem;
//           padding-top: 2rem;
//           border-top: 1px solid rgba(255, 255, 255, 0.2);
//         }

//         .btn-reset {
//           padding: 1rem 2rem;
//           border-radius: 50px;
//           font-weight: 600;
//           border: 2px solid rgba(255, 255, 255, 0.3);
//           background: rgba(255, 255, 255, 0.1);
//           color: white;
//           backdrop-filter: blur(20px);
//           transition: all 0.3s ease;
//         }

//         .btn-reset:hover:not(:disabled) {
//           background: rgba(255, 255, 255, 0.2);
//           border-color: rgba(255, 255, 255, 0.5);
//           transform: translateY(-2px);
//           color: white;
//         }

//         .btn-save {
//           padding: 1rem 3rem;
//           border-radius: 50px;
//           font-weight: 700;
//           font-size: 1.1rem;
//           background: linear-gradient(135deg, #6366f1, #8b5cf6);
//           border: none;
//           color: white;
//           box-shadow: 
//             0 10px 25px rgba(99, 102, 241, 0.3),
//             0 4px 6px rgba(0, 0, 0, 0.1);
//           transition: all 0.3s ease;
//           position: relative;
//           overflow: hidden;
//         }

//         .btn-save::before {
//           content: '';
//           position: absolute;
//           top: 0;
//           left: -100%;
//           width: 100%;
//           height: 100%;
//           background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
//           transition: left 0.5s ease;
//         }

//         .btn-save:hover:not(:disabled) {
//           transform: translateY(-3px);
//           box-shadow: 
//             0 15px 35px rgba(99, 102, 241, 0.4),
//             0 8px 15px rgba(0, 0, 0, 0.15);
//         }

//         .btn-save:hover:not(:disabled)::before {
//           left: 100%;
//         }

//         .btn-spinner {
//           width: 20px;
//           height: 20px;
//           border: 2px solid rgba(255, 255, 255, 0.3);
//           border-top: 2px solid white;
//           border-radius: 50%;
//           animation: spin 1s linear infinite;
//           display: inline-block;
//           margin-right: 0.5rem;
//         }

//         @keyframes spin {
//           0% { transform: rotate(0deg); }
//           100% { transform: rotate(360deg); }
//         }

//         /* Loading Overlay */
//         .loading-overlay {
//           position: fixed;
//           top: 0;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           z-index: 9999;
//         }

//         .loading-container {
//           text-align: center;
//           color: white;
//         }

//         .loading-spinner {
//           position: relative;
//           width: 120px;
//           height: 120px;
//           margin: 0 auto 2rem;
//         }

//         .spinner-ring {
//           position: absolute;
//           width: 100%;
//           height: 100%;
//           border: 4px solid transparent;
//           border-top: 4px solid rgba(255, 255, 255, 0.8);
//           border-radius: 50%;
//           animation: spin 1s linear infinite;
//         }

//         .spinner-ring:nth-child(2) {
//           width: 80%;
//           height: 80%;
//           top: 10%;
//           left: 10%;
//           border-top-color: rgba(255, 255, 255, 0.6);
//           animation-duration: 1.5s;
//           animation-direction: reverse;
//         }

//         .spinner-ring:nth-child(3) {
//           width: 60%;
//           height: 60%;
//           top: 20%;
//           left: 20%;
//           border-top-color: rgba(255, 255, 255, 0.4);
//           animation-duration: 2s;
//         }

//         .loading-title {
//           font-size: 2rem;
//           font-weight: 700;
//           margin-bottom: 1rem;
//         }

//         .loading-subtitle {
//           font-size: 1.1rem;
//           opacity: 0.8;
//           margin-bottom: 2rem;
//         }

//         .loading-progress {
//           width: 300px;
//           height: 4px;
//           background: rgba(255, 255, 255, 0.2);
//           border-radius: 2px;
//           margin: 0 auto;
//           overflow: hidden;
//         }

//         .loading-progress-bar {
//           width: 100%;
//           height: 100%;
//           background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
//           animation: loading-slide 2s infinite;
//         }

//         @keyframes loading-slide {
//           0% { transform: translateX(-100%); }
//           100% { transform: translateX(100%); }
//         }

//         /* Responsive Design */
//         @media (max-width: 768px) {
//           .dashboard-container {
//             padding: 1rem;
//           }

//           .header-content {
//             flex-direction: column;
//             text-align: center;
//           }

//           .header-right {
//             margin-top: 1.5rem;
//           }

//           .section-navigation {
//             flex-direction: column;
//           }

//           .nav-tab {
//             margin-bottom: 0.5rem;
//           }

//           .header-title {
//             font-size: 2rem;
//           }

//           .action-buttons {
//             flex-direction: column;
//             align-items: center;
//           }

//           .btn-save,
//           .btn-reset {
//             width: 100%;
//             max-width: 300px;
//           }
//         }

//         /* Custom SweetAlert Styles */
//         :global(.swal-custom-popup) {
//           border-radius: 20px !important;
//           box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
//         }

//         :global(.swal-custom-title) {
//           font-weight: 700 !important;
//           color: #1f2937 !important;
//         }

//         :global(.swal-custom-content) {
//           color: #6b7280 !important;
//         }
//       `}</style>
//     </>
//   )
// }

// export default EditDashboard


// import React, { useState, useEffect } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import { Form, Button, Card, Row, Col, Spinner, Alert } from "react-bootstrap";
// import Swal from "sweetalert2";

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/api"; // Your Django API base URL

// const EditDashboard = () => {
//   const [profileId, setProfileId] = useState(null); // To store the ID of the current profile
//   const [loading, setLoading] = useState(true);
//   const [saving, setSaving] = useState(false);
//   const [error, setError] = useState(null);
//   // Removed 'success' state as Swal handles success feedback

//   const initialFormData = {
//     what_is_vetrina: "", // Match backend field names
//     what_is_vetzone: "",
//     about_vetrina: "",
//     vision: "",
//     mission: ["", ""], // Default to 2 mission points for the form
//     why_work_with_vetrina: "",
//   };
//   const [formData, setFormData] = useState(initialFormData);

//   useEffect(() => {
//     fetchDashboardContent();
//   }, []);

//   const fetchDashboardContent = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       // Fetch the list of profiles. We assume we're editing the first/only one.
//       const response = await fetch(`${API_BASE_URL}/profiles/`);
//       if (response.ok) {
//         const dataArray = await response.json();
//         if (dataArray && dataArray.length > 0) {
//           const profileData = dataArray[0]; // Take the first profile
//           setProfileId(profileData.id);

//           // Ensure mission array has at least 2 elements for the form, pad if necessary
//           let missionData = profileData.mission || [];
//           if (!Array.isArray(missionData)) missionData = [];
//           while (missionData.length < 2) {
//             missionData.push("");
//           }
//           // If you want to strictly limit to 2 inputs, slice it:
//           // missionData = missionData.slice(0, 2);

//           setFormData({
//             what_is_vetrina: profileData.what_is_vetrina || "",
//             what_is_vetzone: profileData.what_is_vetzone || "",
//             about_vetrina: profileData.about_vetrina || "",
//             vision: profileData.vision || "",
//             mission: missionData, // Use the processed mission data
//             why_work_with_vetrina: profileData.why_work_with_vetrina || "",
//           });
//         } else {
//           // No profile found, admin can create one.
//           console.warn("No existing company profile found. Ready to create a new one.");
//           setProfileId(null); // Ensure no ID is set
//           setFormData(initialFormData); // Reset to initial empty form
//         }
//       } else {
//         console.error("Failed to fetch dashboard content, status:", response.status);
//         setError(`Failed to load content (Status: ${response.status}). You can try creating a new profile.`);
//         setFormData(initialFormData);
//         setProfileId(null);
//       }
//     } catch (error) {
//       console.error("Error fetching dashboard content:", error);
//       setError("Network error or server issue fetching content. You can try creating a new profile.");
//       setFormData(initialFormData);
//       setProfileId(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value,
//     });
//   };

//   const handleMissionChange = (index, value) => {
//     const updatedMission = [...formData.mission];
//     updatedMission[index] = value;
//     setFormData({
//       ...formData,
//       mission: updatedMission,
//     });
//   };

//   // --- Functions to manage mission points if you want dynamic fields ---
//   // const addMissionPoint = () => {
//   //   setFormData({
//   //     ...formData,
//   //     mission: [...formData.mission, ""]
//   //   });
//   // };

//   // const removeMissionPoint = (index) => {
//   //   if (formData.mission.length > 1) { // Keep at least one
//   //     const updatedMission = formData.mission.filter((_, i) => i !== index);
//   //     setFormData({ ...formData, mission: updatedMission });
//   //   }
//   // };
//   // ----------------------------------------------------------------------


//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setSaving(true);
//     setError(null);

//     // Filter out empty mission points before sending to backend if desired
//     const payload = {
//       ...formData,
//       mission: formData.mission.filter(point => point && point.trim() !== ""),
//     };
//     // Or ensure it's an array of strings, even if empty (as per your backend JSONField default=list)
//     // const payload = {
//     //   ...formData,
//     //   mission: formData.mission.map(point => point || ""), // Ensures strings, handles empty
//     // };


//     try {
//       let response;
//       let responseData;

//       if (profileId) {
//         // Update existing profile
//         response = await fetch(`${API_BASE_URL}/profiles/update/${profileId}/`, {
//           method: "PATCH",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//       } else {
//         // Create new profile
//         response = await fetch(`${API_BASE_URL}/profiles/create/`, {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//       }

//       responseData = await response.json();

//       if (response.ok) {
//         Swal.fire({
//           title: "Success!",
//           text: `Dashboard content has been ${profileId ? 'updated' : 'created'} successfully.`,
//           icon: "success",
//           confirmButtonColor: "#8e44ad",
//         });
//         if (!profileId && responseData.id) { // If created, store the new ID
//           setProfileId(responseData.id);
//         }
//         // Optionally, update formData with responseData to reflect any server-side transformations
//         // This is good practice, especially for created_at/updated_at or if the server modifies data
//         let missionData = responseData.mission || [];
//         if (!Array.isArray(missionData)) missionData = [];
//         while (missionData.length < 2) { // Pad for form display
//           missionData.push("");
//         }
//         setFormData({
//             what_is_vetrina: responseData.what_is_vetrina || "",
//             what_is_vetzone: responseData.what_is_vetzone || "",
//             about_vetrina: responseData.about_vetrina || "",
//             vision: responseData.vision || "",
//             mission: missionData,
//             why_work_with_vetrina: responseData.why_work_with_vetrina || "",
//         });

//       } else {
//         const errorDetail = responseData.detail || JSON.stringify(responseData);
//         throw new Error(errorDetail || `Failed to save (Status: ${response.status})`);
//       }
//     } catch (error) {
//       console.error("Error saving dashboard content:", error);
//       const errorMessage = error.message || "An unexpected error occurred.";
//       setError(`Failed to save content: ${errorMessage}`);
//       Swal.fire({
//         title: "Error!",
//         text: `Failed to save content: ${errorMessage}`,
//         icon: "error",
//         confirmButtonColor: "#d33",
//       });
//     } finally {
//       setSaving(false);
//     }
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main-content flex-grow-1 p-lg-4 p-3">
//           <div className="container-fluid">
//             <h1 className="mb-4 page-title-enhanced">Edit Dashboard Content</h1>

//             {error && (
//               <Alert variant="danger" className="mb-4 shadow-sm">
//                 {error}
//               </Alert>
//             )}

//             {loading ? (
//               <div className="text-center my-5">
//                 <Spinner animation="border" variant="primary" style={{width: '3rem', height: '3rem'}}/>
//                 <p className="mt-3 text-muted">Loading editor...</p>
//               </div>
//             ) : (
//               <Form onSubmit={handleSubmit}>
//                 <Row className="g-4">
//                   <Col md={6}>
//                     <Card className="mb-4 editor-card">
//                       <Card.Header as="h5">What is Vetrina</Card.Header>
//                       <Card.Body>
//                         <Form.Group className="mb-0">
//                           <Form.Label visuallyHidden>What is Vetrina Content</Form.Label>
//                           <Form.Control
//                             as="textarea" rows={5} name="what_is_vetrina" // Match backend field
//                             value={formData.what_is_vetrina} onChange={handleInputChange}
//                             placeholder="Enter description for 'What is Vetrina'"
//                           />
//                         </Form.Group>
//                       </Card.Body>
//                     </Card>
//                   </Col>

//                   <Col md={6}>
//                     <Card className="mb-4 editor-card">
//                       <Card.Header as="h5">What is Vetzone</Card.Header>
//                       <Card.Body>
//                         <Form.Group className="mb-0">
//                            <Form.Label visuallyHidden>What is Vetzone Content</Form.Label>
//                           <Form.Control
//                             as="textarea" rows={5} name="what_is_vetzone" // Match backend field
//                             value={formData.what_is_vetzone} onChange={handleInputChange}
//                              placeholder="Enter description for 'What is Vetzone'"
//                           />
//                         </Form.Group>
//                       </Card.Body>
//                     </Card>
//                   </Col>

//                   <Col xs={12}>
//                      <Card className="mb-4 editor-card">
//                        <Card.Header as="h5">About Vetrina</Card.Header>
//                        <Card.Body>
//                          <Form.Group className="mb-0">
//                            <Form.Label visuallyHidden>About Vetrina Content</Form.Label>
//                            <Form.Control
//                              as="textarea" rows={7} name="about_vetrina" // Match backend field
//                              value={formData.about_vetrina} onChange={handleInputChange}
//                               placeholder="Enter the 'About Vetrina' text"
//                            />
//                          </Form.Group>
//                        </Card.Body>
//                      </Card>
//                   </Col>

//                   <Col md={6}>
//                     <Card className="mb-4 editor-card">
//                       <Card.Header as="h5">Vision</Card.Header>
//                       <Card.Body>
//                         <Form.Group className="mb-0">
//                            <Form.Label visuallyHidden>Vision Content</Form.Label>
//                           <Form.Control
//                             as="textarea" rows={4} name="vision" // Match backend field
//                             value={formData.vision} onChange={handleInputChange}
//                              placeholder="Enter the company vision statement"
//                           />
//                         </Form.Group>
//                       </Card.Body>
//                     </Card>
//                   </Col>

//                   <Col md={6}>
//                     <Card className="mb-4 editor-card">
//                       <Card.Header as="h5">Mission</Card.Header>
//                       <Card.Body>
//                         {/* Map over formData.mission to create inputs */}
//                         {formData.mission.map((point, index) => (
//                           <Form.Group className="mb-3" key={index}>
//                             <Form.Label>{`Mission Point ${index + 1}`}</Form.Label>
//                             <Form.Control
//                               type="text" value={point}
//                               onChange={(e) => handleMissionChange(index, e.target.value)}
//                               placeholder={`Enter mission point ${index + 1}`}
//                             />
//                             {/* Optional: Add a remove button if you implement dynamic mission points */}
//                             {/* {formData.mission.length > 1 && (
//                               <Button variant="link" size="sm" className="text-danger p-0 float-end" onClick={() => removeMissionPoint(index)}>
//                                 Remove
//                               </Button>
//                             )} */}
//                           </Form.Group>
//                         ))}
//                         {/* Optional: Add button if you implement dynamic mission points */}
//                         {/* <Button variant="outline-secondary" size="sm" onClick={addMissionPoint}>
//                           <i className="fas fa-plus me-1"></i> Add Mission Point
//                         </Button> */}
//                       </Card.Body>
//                     </Card>
//                   </Col>

//                   <Col xs={12}>
//                      <Card className="mb-4 editor-card">
//                        <Card.Header as="h5">Why Work With Vetrina</Card.Header>
//                        <Card.Body>
//                          <Form.Group className="mb-0">
//                            <Form.Label visuallyHidden>Why Work With Vetrina Content</Form.Label>
//                            <Form.Control
//                              as="textarea" rows={7} name="why_work_with_vetrina" // Match backend field
//                              value={formData.why_work_with_vetrina} onChange={handleInputChange}
//                               placeholder="Explain the reasons to work with Vetrina"
//                            />
//                          </Form.Group>
//                        </Card.Body>
//                      </Card>
//                   </Col>
//                 </Row>

//                 <div className="d-flex justify-content-end mb-4 editor-actions">
//                   <Button
//                     variant="outline-secondary" className="me-3"
//                     onClick={fetchDashboardContent} // Re-fetch original/saved data
//                     disabled={loading || saving}
//                   >
//                     <i className="fas fa-undo me-2"></i>Reset Form
//                   </Button>
//                   <Button variant="primary-gradient" type="submit" disabled={saving || loading} className="btn-lg">
//                     {saving ? (
//                       <>
//                         <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
//                         Saving...
//                       </>
//                     ) : (
//                      <> <i className="fas fa-save me-2"></i> {profileId ? "Save Changes" : "Create Profile"} </>
//                     )}
//                   </Button>
//                 </div>
//               </Form>
//             )}
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         /* Your existing styles - ensure CSS variables are defined or replace them */
//         :root { /* Example CSS variables, ensure they match your global setup or replace */
//            --primary-color: #8e44ad;
//            --secondary-color: #3498db;
//            --highlight-color: #9b59b6;
//            --text-dark: #333;
//            --text-light: #555;
//            --text-muted: #6c757d;
//            --background-light: #f9f9f9;
//            --border-color: #e0e0e0;
//            --primary-gradient: linear-gradient(to right, #9b59b6, #8e44ad);
//            --card-bg: white;
//            --light-gray: #f8f9fa;
//            --text-color: #34495e; /* General text */
//            --border-radius-md: 0.5rem;
//            --border-radius-sm: 0.25rem;
//            --shadow-sm: 0 2px 5px rgba(0,0,0,0.05);
//            --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
//            --shadow-lg: 0 8px 25px rgba(142, 68, 173, 0.15);
//         }
//         .main-content { background-color: var(--background-light, #f9fafd); }
//         .page-title-enhanced { font-weight: 700; color: var(--text-color, #34495e); margin-bottom: 2.5rem; position: relative; padding-bottom: 10px; }
//         .page-title-enhanced:after { content: ''; position: absolute; bottom: 0; left: 0; width: 80px; height: 4px; background: var(--primary-gradient, linear-gradient(to right, #9b59b6, #8e44ad)); border-radius: 2px; }
//         .editor-card { border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--border-radius-md, 0.5rem); box-shadow: var(--shadow-sm, 0 2px 5px rgba(0,0,0,0.05)); transition: box-shadow 0.3s ease; background-color: var(--card-bg, white); }
//         .editor-card:focus-within { box-shadow: var(--shadow-md, 0 4px 12px rgba(0,0,0,0.08)); border-left: 4px solid var(--primary-color, #8e44ad); }
//         .editor-card .card-header { background-color: var(--light-gray, #f8f9fa); border-bottom: 1px solid var(--border-color, #e0e0e0); font-weight: 600; color: var(--text-color, #34495e); font-size: 1.1rem; padding: 0.8rem 1.25rem; }
//         .form-label { font-weight: 500; color: var(--text-muted, #7f8c8d); margin-bottom: 0.5rem; font-size: 0.9rem; }
//         .form-control { border-radius: var(--border-radius-sm, 0.25rem); border: 1px solid var(--border-color, #ced4da); padding: 0.75rem 1rem; transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out; font-size: 0.95rem; color: var(--text-color); }
//         .form-control:focus { border-color: var(--primary-color, #8e44ad); box-shadow: 0 0 0 0.2rem rgba(142, 68, 173, 0.25); }
//         textarea.form-control { line-height: 1.6; }
//         .editor-actions .btn { border-radius: 50px; font-weight: 600; padding: 10px 25px; }
//         .editor-actions .btn-outline-secondary { border-color: var(--border-color); color: var(--text-muted); }
//         .editor-actions .btn-outline-secondary:hover { background-color: var(--light-gray); color: var(--text-color); }
//         .btn-primary-gradient { background-image: var(--primary-gradient, linear-gradient(to right, #9b59b6, #8e44ad)); color: white; border: none; box-shadow: var(--shadow-md, 0 4px 12px rgba(0,0,0,0.08)); transition: all 0.3s ease; }
//         .btn-primary-gradient:hover { box-shadow: var(--shadow-lg, 0 8px 25px rgba(142, 68, 173, 0.15)); transform: translateY(-2px); }
//       `}</style>
//     </>
//   );
// };

// export default EditDashboard;